// "Is this person already in the CRM?" — asked BEFORE Add Contact saves.
//
// Add Contact never looked. The only guard was the id slug: a second
// "Jane Doe" quietly became `con-jane-doe-2`, and the duplicate review found
// it days later — after invitations, notes and a primary-contact link had been
// hung on whichever copy the person happened to open. (One of the two new
// pairs the review showed on 2026-09-28 was exactly this: a `-2` record.)
//
// The rule is deliberately the same shape as the server's duplicate review
// (routes/contacts-admin.ts GET /duplicates), so the form warns about exactly
// what the review would later flag:
//   email  the same address, work OR personal, on either side — trimmed and
//          case-insensitive. Only something that looks like an address (has
//          an "@") takes part, so a half-typed field matches nothing.
//   name   the same first + last name after trimming, lower-casing and
//          collapsing inner spaces — in EITHER ORDER, because the symposium
//          import swapped first and last names on real rows, and the swapped
//          twin is the duplicate most worth catching.
//
// Pure (no store, no React) so it can be checked on its own.

import type { Contact } from '@/types/entities'

export type ContactMatchReason = 'email' | 'name'

export interface ContactMatch {
  contact: Contact
  /** Why it matched; 'email' first when both apply. */
  reasons: ContactMatchReason[]
  /** The address(es) the two share, normalised — empty for a name-only match. */
  sharedEmails: string[]
}

/** The draft fields the matcher reads — a subset of Contact. */
export interface ContactMatchDraft {
  firstName?: string
  lastName?: string
  workEmail?: string
  personalEmail?: string
}

export const normEmail = (v?: string | null): string => (v ?? '').trim().toLowerCase()

export const normNamePart = (v?: string | null): string =>
  (v ?? '').trim().replace(/\s+/g, ' ').toLowerCase()

const emailsOf = (c: ContactMatchDraft): string[] =>
  [...new Set([normEmail(c.workEmail), normEmail(c.personalEmail)].filter((e) => e.includes('@')))]

/**
 * Existing contacts that look like the person being entered, best evidence
 * first: shared-address matches before name-only ones, then by name. A
 * contact matched on both counts appears once, carrying both reasons.
 * `excludeId` leaves out the record being edited (a contact always matches
 * itself).
 */
export function findExistingContacts(
  draft: ContactMatchDraft,
  contacts: readonly Contact[],
  opts: { excludeId?: string } = {},
): ContactMatch[] {
  const draftEmails = new Set(emailsOf(draft))
  const first = normNamePart(draft.firstName)
  const last = normNamePart(draft.lastName)
  const nameComplete = first !== '' && last !== ''
  const exclude = (opts.excludeId ?? '').toLowerCase()
  if (!draftEmails.size && !nameComplete) return []

  const out: ContactMatch[] = []
  for (const c of contacts) {
    if (exclude && c.id.toLowerCase() === exclude) continue
    const reasons: ContactMatchReason[] = []
    const sharedEmails = emailsOf(c).filter((e) => draftEmails.has(e))
    if (sharedEmails.length) reasons.push('email')
    if (nameComplete) {
      const cf = normNamePart(c.firstName)
      const cl = normNamePart(c.lastName)
      if (cf && cl && ((cf === first && cl === last) || (cf === last && cl === first))) {
        reasons.push('name')
      }
    }
    if (reasons.length) out.push({ contact: c, reasons, sharedEmails })
  }
  const label = (m: ContactMatch) =>
    `${normNamePart(m.contact.lastName)} ${normNamePart(m.contact.firstName)} ${m.contact.id}`
  return out.sort((a, b) =>
    Number(b.reasons.includes('email')) - Number(a.reasons.includes('email'))
    || label(a).localeCompare(label(b)))
}
